import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { History, Mail, MailCheck, MailX, FileText, Paperclip, AlertTriangle, ArrowRight, RefreshCw, Users } from "lucide-react";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";

const TYPE_SHORT = { initial: "Entretien initial", suivi_tcc: "Suivi TCC", therapie_couple: "Couple", suivi_tcc_ado: "Suivi ado" };
const fmtDay = (d) => formatDateSafe(d, { day: "numeric", month: "short" });
const fmtDateTime = (d) => formatDateSafe(d, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const EmailBadge = ({ s }) => {
  const fu = s.followup;
  if (fu.status === "envoye") {
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`recent-email-${s.report_id}`}><MailCheck className="w-3.5 h-3.5" /> E-mail envoyé {fmtDateTime(fu.sent_at)}{fu.partial ? " (1 partenaire)" : ""}</span>;
  }
  if (fu.status === "genere") {
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300" data-testid={`recent-email-${s.report_id}`}><Mail className="w-3.5 h-3.5" /> Brouillon généré, non envoyé</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`recent-email-${s.report_id}`}><MailX className="w-3.5 h-3.5" /> E-mail non généré</span>;
};

const DocsBadge = ({ s }) => {
  if (s.attachments_count > 0) {
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`recent-docs-${s.report_id}`}><Paperclip className="w-3.5 h-3.5" /> {s.attachments_count} document{s.attachments_count > 1 ? "s" : ""} joint{s.attachments_count > 1 ? "s" : ""}</span>;
  }
  if (s.pending_worksheets > 0) {
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300" data-testid={`recent-docs-${s.report_id}`}><FileText className="w-3.5 h-3.5" /> {s.pending_worksheets} exercice{s.pending_worksheets > 1 ? "s" : ""} assigné{s.pending_worksheets > 1 ? "s" : ""}, fiche non envoyée</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`recent-docs-${s.report_id}`}><FileText className="w-3.5 h-3.5" /> Aucun document</span>;
};

const SessionRow = ({ s }) => (
  <div className={`flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border px-3 py-2 ${s.overdue ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "bg-background"}`} data-testid={`recent-session-${s.report_id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm truncate">{s.patient_name || "Patient"}</span>
        {s.couple && <Users className="w-3.5 h-3.5 text-pink-500" title="Thérapie de couple" />}
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{TYPE_SHORT[s.session_type] || s.session_type}</span>
        <span className="text-xs text-muted-foreground">{fmtDay(s.session_date)}</span>
        {s.overdue && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200" data-testid={`recent-overdue-${s.report_id}`}><AlertTriangle className="w-3 h-3" /> +72 h sans e-mail</span>}
        {s.missing_email && <span className="text-[10px] text-rose-600">adresse e-mail manquante</span>}
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-1"><EmailBadge s={s} /><DocsBadge s={s} /></div>
    </div>
    <Link to={`/therapist/patients/${s.patient_id}?session=${s.report_id}`} className="shrink-0">
      <Button size="sm" variant="outline" className="gap-1.5 w-full sm:w-auto" data-testid={`recent-open-${s.report_id}`}>Ouvrir la séance <ArrowRight className="w-3.5 h-3.5" /></Button>
    </Link>
  </div>
);

const RecentSessionsCard = () => {
  const [data, setData] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    axios.get(`${API}/therapist/recent-sessions`, { withCredentials: true, params: { days: 14 } }).then((r) => setData(r.data)).catch(() => setData({ sessions: [], pending_count: 0, overdue_count: 0 })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("kintsugi:calendly-synced", h); return () => window.removeEventListener("kintsugi:calendly-synced", h); }, []);
  const sessions = (data?.sessions || []).filter((s) => !pendingOnly || s.followup.status !== "envoye");
  return (
    <Card data-testid="recent-sessions-card">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3">
        <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2"><History className="w-5 h-5 text-primary" /> Dernières séances <span className="text-xs font-normal text-muted-foreground">(14 derniers jours)</span></CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          {data && <span className="text-xs text-muted-foreground" data-testid="recent-sessions-counts">{data.pending_count} à traiter{data.overdue_count ? ` · ${data.overdue_count} en retard (+72 h)` : ""}</span>}
          <Button size="sm" variant={pendingOnly ? "default" : "outline"} onClick={() => setPendingOnly((v) => !v)} className="gap-1.5" data-testid="recent-sessions-filter-pending"><MailX className="w-3.5 h-3.5" /> E-mail non envoyé</Button>
          <Button size="icon" variant="ghost" onClick={load} disabled={loading} title="Actualiser" data-testid="recent-sessions-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {!data ? <p className="text-sm text-muted-foreground">Chargement…</p>
          : sessions.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="recent-sessions-empty">{pendingOnly ? "Toutes les séances récentes ont reçu leur e-mail de suivi." : "Aucune séance au cours des 14 derniers jours."}</p>
          : sessions.map((s) => <SessionRow key={s.report_id} s={s} />)}
      </CardContent>
    </Card>
  );
};

export default RecentSessionsCard;
